import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, Loader2, Mail, CreditCard } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem("mf_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const [leads, setLeads] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true, headers: authHeaders() })
      .then((res) => {
        setUser(res.data);
        setChecking(false);
      })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, []);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/leads`, { withCredentials: true, headers: authHeaders() }).then((r) => setLeads(r.data)).catch(() => {});
    axios.get(`${API}/admin/payments`, { withCredentials: true, headers: authHeaders() }).then((r) => setPayments(r.data)).catch(() => {});
  }, [user]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers: authHeaders() }).catch(() => {});
    localStorage.removeItem("mf_token");
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-page">
      <header className="border-b border-ink/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex flex-col leading-none" data-testid="admin-logo-link">
            <span className="font-serif text-xl font-medium tracking-tight text-ink">
              Marketing <span className="italic text-accent">Facile</span>
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-mutedink">Area riservata — {user?.email}</span>
          </Link>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-ink hover:text-white"
          >
            <LogOut className="h-3.5 w-3.5" /> Esci
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${tab === "leads" ? "bg-ink text-white" : "border border-ink/20 text-ink hover:bg-ink hover:text-white"}`}
          >
            <Mail className="h-3.5 w-3.5" /> Richieste ({leads.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("payments")}
            data-testid="admin-tab-payments"
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${tab === "payments" ? "bg-ink text-white" : "border border-ink/20 text-ink hover:bg-ink hover:text-white"}`}
          >
            <CreditCard className="h-3.5 w-3.5" /> Pagamenti ({payments.length})
          </button>
        </div>

        {tab === "leads" && (
          <div className="mt-8 divide-y divide-ink/10 border border-ink/12 bg-white" data-testid="admin-leads-list">
            {leads.length === 0 && <p className="p-8 text-sm text-mutedink">Nessuna richiesta per ora.</p>}
            {leads.map((l) => (
              <div key={l.id} className="p-6" data-testid={`admin-lead-${l.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-serif text-xl text-ink">{l.name} <span className="text-sm text-mutedink">— {l.company}</span></p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">{new Date(l.created_at).toLocaleDateString("it-IT")}</p>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-mutedink">
                  <a href={`mailto:${l.email}`} className="text-accent hover:underline">{l.email}</a>
                  {l.phone && <a href={`tel:${l.phone}`} className="hover:text-ink">{l.phone}</a>}
                  {l.website && <a href={l.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{l.website}</a>}
                  <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{l.interest}</span>
                  {l.start_when && <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{l.start_when}</span>}
                  {l.budget && <span className="font-mono text-[10px] uppercase tracking-[0.15em]">{l.budget}</span>}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-ink/80">{l.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "payments" && (
          <div className="mt-8 divide-y divide-ink/10 border border-ink/12 bg-white" data-testid="admin-payments-list">
            {payments.length === 0 && <p className="p-8 text-sm text-mutedink">Nessun pagamento per ora.</p>}
            {payments.map((p) => (
              <div key={p.session_id} className="flex flex-wrap items-center justify-between gap-3 p-6" data-testid={`admin-payment-${p.session_id}`}>
                <div>
                  <p className="font-serif text-xl text-ink">€{(p.amount / 100).toLocaleString("it-IT")} <span className="text-sm text-mutedink">— {p.reference}</span></p>
                  <p className="mt-1 text-sm text-mutedink">{p.name} · {p.email}</p>
                </div>
                <span className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${p.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-ink/5 text-mutedink"}`}>
                  {p.payment_status}
                </span>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
